'use client';

/**
 * UserMenu — custom avatar dropdown for signed-in users.
 *
 * Replaces Clerk's default <UserButton> with a shadcn DropdownMenu so we can
 * add first-party items: view profile, settings, theme cycle, about page,
 * and sign out.
 */

import { useClerk, useUser } from '@clerk/nextjs';
import { useTheme } from 'next-themes';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateCurrentUser } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sun, Moon, Monitor, User, Settings, Info, LogOut } from 'lucide-react';

const THEME_CYCLE: Record<string, { next: string; icon: React.ReactNode; label: string }> = {
  light:   { next: 'dark',   icon: <Moon className="h-4 w-4" />,    label: 'Switch to dark'   },
  dark:    { next: 'system', icon: <Monitor className="h-4 w-4" />, label: 'Switch to system' },
  system:  { next: 'light',  icon: <Sun className="h-4 w-4" />,     label: 'Switch to light'  },
};

export function UserMenu() {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!isLoaded || !user) return null;

  const initials = (user.fullName ?? user.username ?? 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const currentTheme = mounted ? (theme ?? 'system') : 'system';
  const themeAction = THEME_CYCLE[currentTheme] ?? THEME_CYCLE.system;

  // Use Clerk username for the /u/[username] profile route
  const profileHref = user.username ? `/u/${user.username}` : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full ring-2 ring-transparent hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
          aria-label="Account menu"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.imageUrl} alt={user.fullName ?? 'Avatar'} />
            <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-52">
        {/* Identity */}
        <div className="px-3 py-2">
          <p className="text-sm font-semibold truncate">{user.fullName ?? user.username}</p>
          <p className="text-xs text-muted-foreground truncate">
            {user.primaryEmailAddress?.emailAddress}
          </p>
        </div>

        <DropdownMenuSeparator />

        {profileHref && (
          <DropdownMenuItem onClick={() => router.push(profileHref)}>
            <User className="h-4 w-4 mr-2" />
            View profile
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={() => router.push('/settings')}>
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </DropdownMenuItem>

        {/* Theme cycle — also persists so UserPreferencesProvider doesn't overwrite it */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            const next = themeAction.next as 'light' | 'dark' | 'system';
            setTheme(next);
            updateCurrentUser({ preferences: { theme: next } }).then(() => {
              queryClient.invalidateQueries({ queryKey: ['user', 'preferences'] });
            });
          }}
        >
          <span className="mr-2">{themeAction.icon}</span>
          {themeAction.label}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => router.push('/about')}>
          <Info className="h-4 w-4 mr-2" />
          About Favely
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => signOut({ redirectUrl: '/' })}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
